import {
  MAIN_JOBS_ENTITY,
  RUNNING_JOBS_ENTITY,
  QUEUED_JOBS_ENTITY,
  NON_AGGREGATED_JOBS_ENTITY,
  RECENT_JOBS_ENTITY,
  HISTORY_JOBS_ENTITY,
} from '../Entities';

const JOB_STATUS_UNKOWN = 'UNKOWN';
const JOB_STATUS_QUEUED = 'QUEUED';
const JOB_STATUS_RUNNING = 'RUNNING';
const JOB_STATUS_ABORTED = 'ABORTED';
const JOB_STATUS_FAILED = 'FAILED';
const JOB_STATUS_COMPLETED = 'COMPLETED';
const JOB_STATUS_REJECTED = 'REJECTED';
const JOB_STATUS_DISCONNECTED = 'DISCONNECTED';
const JOB_STATUS_IDLETIMEOUT = 'IDLETIMEOUT';

const JOB_STATUS_QUEUED_VALUE = 1;
const JOB_STATUS_RUNNING_VALUE = 2;

const JOB_STATUSES = [
  { value: JOB_STATUS_QUEUED_VALUE, name: JOB_STATUS_QUEUED, label: 'Queued', filter: false },
  { value: JOB_STATUS_RUNNING_VALUE, name: JOB_STATUS_RUNNING, label: 'Running' },
  { value: 3, name: JOB_STATUS_ABORTED, label: 'Aborted' },
  { value: 4, name: JOB_STATUS_FAILED, label: 'Failed' },
  { value: 5, name: JOB_STATUS_COMPLETED, label: 'Completed' },
  { value: 6, name: JOB_STATUS_REJECTED, label: 'Rejected', filter: false },
  { value: 7, name: JOB_STATUS_DISCONNECTED, label: 'Disconnected' },
  { value: 8, name: JOB_STATUS_IDLETIMEOUT, label: 'Idle Timeout' },
];

const JOB_SOLVE_STATUS_INIT = 'init';
const JOB_SOLVE_STATUS_COMPLETED = 'completed';
const JOB_SOLVE_STATUSES = [
  { value: 1, name: JOB_SOLVE_STATUS_INIT, label: 'Init' },
  { value: 3, name: JOB_SOLVE_STATUS_COMPLETED, label: 'Completed' },
];

const MAIN_JOBS = 'mainJobs';
const FILTERED_MAIN_JOBS = 'filteredMainJobs';
const QUEUED_JOBS = 'queuedJobs';
const HISTORY_JOBS = 'historyJobs';
const JOB_TYPES = [MAIN_JOBS, QUEUED_JOBS, HISTORY_JOBS];

// Job actions, must match action ids in "permissions" node of the app JSON config file.
// Refer t
const ACTION_ABORT_JOB = 'abortJob';

class JobService {
  constructor(config, store, options) {
    this.apiClient = config.apiClient;
    this.apiClientPrefix = config.apiClientPrefix;
    this.authService = config.authService;
    this.prefix = config.prefix;

    const { type = 'Job' } = options;
    const computeOptions = (opts, defaultURI) => {
      const polling = opts && opts.polling || {};
      return {
        etag: opts && opts.etag,
        polling: polling.defaultDelay,
        runningPolling: polling.runningDelay,
        paging: opts && opts.paging,
        uri: opts && opts.uri || defaultURI,
      };
    };
    const defaultOpts = computeOptions(options);
    const { uri, ...recentOptions } = {
      ...defaultOpts,
      ...computeOptions(options.recent, options.uri || '/jobs'),
    };
    const { uri: historyUri, ...historyOptions } = {
      ...defaultOpts,
      ...computeOptions(options.history, options.historyUri || '/history/jobs/recent'),
    };
    this.uri = uri;
    this.historyUri = historyUri;
    this.store = store;

    // Register computed properties
    const { schemaService } = config;
    schemaService.addComputedProperty({
      name: 'jobType',
      typename: type,
      label: 'Type',
      type: 'string',
      compute: (job) => {
        return this.getJobAlgorithm(job);
      },
    });

    if (options && options.addUserEmail) {
      schemaService.addComputedProperty({
        name: 'userEmail',
        typename: type,
        label: 'User',
        type: 'string',
        entities: ['userEmails'],
        compute: (job, { userEmails }) => {
          return userEmails[job.ownerId];
        },
      });
    }

    // Register Job entities
    const { nonAggregate = true, batch = true } = options || {};
    this.allowNonAggregate = nonAggregate;

    this.sortJobs = this.sortJobs.bind(this);

    store.addEntity(RECENT_JOBS_ENTITY, {
      ...recentOptions,
      request: context => this.requestRecentJobs(context),
      getPolling: recentOptions.polling ? ({ [RUNNING_JOBS_ENTITY]: runningJobs, [QUEUED_JOBS_ENTITY]: queuedJobs }) => {
        return runningJobs.length || queuedJobs.length ? recentOptions.runningPolling : recentOptions.polling;
      } : undefined,
      type: schemaService.getType(type),
      title: 'recent jobs',
      defaultValue: [],
      derivedEntities: {
        entities: [RUNNING_JOBS_ENTITY, QUEUED_JOBS, MAIN_JOBS_ENTITY],
        compute: this.sortJobs,
      },
      computeDiffs: true,
    });

    if (nonAggregate) {
      store.addEntity(NON_AGGREGATED_JOBS_ENTITY, {
        request: context => this.requestNonAggregatedRecentJobs(context.query),
        ...recentOptions,
        type: schemaService.getType(type),
        title: 'non aggregated jobs',
        defaultValue: [],
        computeDiffs: true,
        clearOnUnsubscribe: true, // When the last subscriber unsubscribes from this entity, we clear data and stop pooling
      });
    }

    store.addEntity(HISTORY_JOBS_ENTITY, {
      ...historyOptions,
      request: historyOptions.paging
        ? (context => this.requestHistoryJobsPage(context))
        : (context => this.requestHistoryJobs(context.query)),
      type: schemaService.getType(type),
      title: 'history jobs',
      defaultValue: [],
    });

    this.historyJobsSubs = [];
    this.historyJobs = undefined;
    this.historyJobsById = {};
    this.historyJobsPms = undefined;
    this.cancelledPromises = [];

    this.abortingJobs = {};
    this.jobLogs = {};
    this.statuses = options.statuses || JOB_STATUSES;
    this.statusLabels = [];
    this.statusNames = [];
    this.statuses.forEach((st) => {
      this.statusLabels[st.value] = st.label;
      this.statusNames[st.value] = st.name;
    });
    this.batch = batch;
    this.jobMetrics = {};
  }

  sortJobs(jobs) {
    const mainJobs = [];
    const queuedJobs = [];
    const runningJobs = [];
    jobs.forEach((job) => {
      if (job.status === JOB_STATUS_QUEUED_VALUE) {
        queuedJobs.push(job);
      } else {
        mainJobs.push(job);
        if (job.status === JOB_STATUS_RUNNING_VALUE) {
          runningJobs.push(job);
        }
      }
    });
    // const { mainJobs } = sortedJobs;
    // if (saved.savedMainJobs) {
    //   const newObjs = [];
    //   const changedObj = [];
    //   mainJobs.forEach((job) => {
    //     const oldJob = saved.savedMainJobs.find(j => j.id === job.id);
    //     if (oldJob) {
    //       if (oldJob !== job) changedObj.push(job.id);
    //     } else {
    //       newObjs.push(job.id);
    //     }
    //   });
    //   console.log('\nDiffs: ');
    //   if (newObjs.length) {
    //     console.log('    New objects: ', newObjs.join(','));
    //   }
    //   if (changedObj.length) {
    //     console.log('    Changed objects: ', changedObj.join(','));
    //   }
    // }
    // saved.savedMainJobs = mainJobs;
    return { mainJobs, queuedJobs, runningJobs };
  }

  validateAbort(job) {
    if (this.abortingJobs[job.id]) {
      return `Job ${this.getJobName(job)} is already aborting`;
    }
    const authResponse = this.authService.canPerformAction(ACTION_ABORT_JOB, job, this.getJobUserId(job));
    if ((authResponse !== true) && (authResponse !== null)) {
      return authResponse;
    }
    if (!this.jobStatusAbortable(job)) {
      return `Job ${this.getJobName(job)} must be running or queued to be aborted`;
    }
    return true;
  }

  jobStatusAbortable(job) {
    const { status } = job;
    return (status === JOB_STATUS_QUEUED_VALUE) || (status === JOB_STATUS_RUNNING_VALUE);
  }

  isJobRunning(job) { return job.status === JOB_STATUS_RUNNING_VALUE; }

  isJobWaiting(job) { return job.status === JOB_STATUS_QUEUED_VALUE; }

  isJobCompleted(job) {
    const { status } = job;
    return (status !== JOB_STATUS_QUEUED_VALUE) && (status !== JOB_STATUS_RUNNING_VALUE);
  }

  abortJob(job) {
    this.abortingJobs[job.id] = true;
    return this.requestAbortJob(job.id, job)
      .finally(() => { delete this.abortingJobs[job.id]; });
  }

  requestAbortJob(jobId) {
    return this.apiClient.delete(`${this.uri}/${jobId}/processing`);
  }

  printError(error) {
    // Process error if it contains an error object, generated by the optimization server
    if (!error.response || !error.response.data || !error.response.data.error) {
      return error.message || error;
    }
    const processedError = Object.assign({}, error.response.data.error);
    if (processedError.code) {
      return `[${processedError.code}] ${processedError.message}`;
    }
    return processedError.message;
  }

  abortJobs(jobs) {
    // Replace worker jobs with their parent job
    const jobsById = {};
    const parentJobIds = [];
    // Extract worker jobs from the list
    const abortJobs = jobs.filter((job) => {
      if (job.properties.parentId) {
        parentJobIds.push(job.properties.parentId);
        return false;
      }
      jobsById[job.id] = job;
      return true;
    });
    // Insert worker job parents if not already in the list
    parentJobIds.forEach((jobId) => {
      if (!abortJobs.find(job => job.id === jobId)) {
        // Abort jobs can only be performed from recent job list.
        // We can rely on the fact that parent of recent worker job are also
        // on recent job list.
        const parentJob = this.recentJobsPooling.data.find(job => job.id === jobId);
        if (parentJob) {
          abortJobs.push(parentJob);
        }
      }
    });
    // A complete validation is performed before a call to abortJobs,
    // with validateAbort
    const jobErrors = {};
    return Promise.all(
      abortJobs.map(job => this.abortJob(job).catch((err) => { jobErrors[job.id] = err; })),
    ).then(() => {
      this.store.update(RECENT_JOBS_ENTITY);
      const failedJobIds = Object.keys(jobErrors);
      if (failedJobIds.length) {
        return Promise.reject(new Error(failedJobIds.map(id => this.printError(jobErrors[id])).join('\n')));
      }
      return true;
    });
  }

  hasLog(job) {
    const { status } = job;
    return (status !== JOB_STATUS_QUEUED_VALUE);
  }

  subscribeJobLog(id, job, subscriber) {
    let node = this.jobLogs[id];
    if (!node) {
      node = {
        first: true,
        job,
        status: {
          full: false,
          p: undefined,
          log: '',
        },
        subscribers: [subscriber],
        stop: () => {
          if (node.timeOutId) {
            clearTimeout(node.timeOutId);
            delete node.timeOutId;
          }
        },
        update: () => {
          const params = {};
          if (node.first) {
            if (node.status.full) {
              params.b = true;
            } else {
              params.n = 50;
            }
          } else {
            params.p = node.status.index;
          }
          this.requestJobLog(id, job, params).then((res) => {
            node.status = Object.assign({}, node.status, {
              error: null,
              log: node.first ? res.data : (node.status.log + res.data),
              index: res.headers['x-gurobi-index'],
            });
            node.first = false;
            node.subscribers.forEach(s => s(node.status));
            if (node.status.index !== '-1') {
              node.timeOutId = setTimeout(node.update, 1000);
            } else {
              delete node.timeOutId;
            }
          }).catch((e) => {
            if (!node.status.error) {
              node.status.error = e;
              node.subscribers.forEach(s => s(node.status));
            }
            node.timeOutId = setTimeout(node.update, 1000);
          });
        },
      };
      this.jobLogs[id] = node;
      node.update();
    } else {
      node.subscribers.push(subscriber);
    }
    return node.status;
  }

  requestJobLog(jobId, job, params) {
    return this.apiClient.get(`${this.uri}/${jobId}/log`, { params });
  }

  unsubscribeJobLog(jobId, subscriber) {
    const node = this.jobLogs[jobId];
    if (node) {
      const index = node.subscribers.indexOf(subscriber);
      if (index >= 0) {
        node.subscribers.splice(index, 1);
        if (node.subscribers.length === 0) {
          this.jobLogs[jobId].stop();
          delete this.jobLogs[jobId];
        }
      }
    }
  }

  fetchFullLog(id) {
    const node = this.jobLogs[id];
    if (node) {
      node.stop();
      node.first = true;
      node.status = Object.assign({}, node.status, { full: true });
      node.update();
    }
  }

  getJobLogURL(job) { return `${this.prefix || ''}/jobs/log?id=${job.id}`; }

  getJobDashboardURL(job) { return `${this.prefix || ''}/jobs/dashboard?id=${job.id}`; }

  getJobs(jobType) {
    if (jobType === HISTORY_JOBS) {
      return this.historyJobs || [];
    }
    const jobs = this.recentJobsPooling.data;
    return jobType && this.sortJobs(jobs)[jobType] || jobs;
  }

  getJob(id) {
    return this.requestJob(id).then(res => (res.data.length ? res.data[0] : res.data));
  }

  requestJob(jobId) {
    return this.apiClient.get(`${this.uri}/${jobId}`);
  }

  getJobWithType(id) {
    if (!id) {
      return [];
    }
    const job = this.recentJobsPooling.data.find(j => j.id === id);
    if (job) {
      const type = job.status === JOB_STATUS_QUEUED_VALUE ? QUEUED_JOBS : MAIN_JOBS;
      return [job, type];
    }
    return [(this.historyJobs || []).find(j => j.id === id), HISTORY_JOBS];
  }

  getDownloadLogURL(job) {
    return `${this.apiClientPrefix}/jobs/${job.id}/log?b=true`;
  }

  fetchJobParameters(jobId, job) {
    return this.requestJobParameters(jobId, job).then(res => (res.data || []));
  }

  requestJobParameters(jobId) {
    return this.apiClient.get(`${this.uri}/${jobId}/parameters`);
  }

  filterRecentJobsOnNode(nodeAddress, statuses) {
    if (this.clusterJobsRoute) {
      const ctx = this.clusterJobsRoute.getContext();
      const { filter } = ctx || {};
      if (!filter || (filter.address !== nodeAddress) || statuses) {
        this.clusterJobsRoute.setContext(Object.assign({}, ctx, {
          filter: Object.assign({}, filter, { address: nodeAddress, jobstatus: statuses }),
        }));
      }
    }
  }

  useNonAggregate() {
    return this.allowNonAggregate;
  }

  setAggregateQueue(aggregate) {
    this.aggregateQueue = aggregate;
    window.localStorage.setItem('aggregate-queue', aggregate ? 'true' : 'false');
    // Queue jobs in the pooling are now invalid, we remove them
    this.recentJobsPooling.filterData(job => job.status !== JOB_STATUS_QUEUED_VALUE);
    return this.startRecentJobsPolling();
  }

  requestRecentJobs({ query }) {
    const recentParams = 'aggregate=true&running=true&recent=true&queued=true';
    return query
      ? this.apiClient.get(`${this.uri}${query}&${recentParams}`)
      : this.apiClient.get(`${this.uri}?${recentParams}`);
  }

  requestNonAggregatedRecentJobs(query) {
    const aggQuery = query ? `${query}&aggregate=false` : '?aggregate=false';
    return this.apiClient.get(`${this.uri}${aggQuery}`);
  }

  startRecentJobsPolling() {
    return this.recentJobsPooling.start();
  }

  requestHistoryJobs(query/* , queryParams */) {
    return this.apiClient.get(`${this.historyUri}${query || ''}`);
  }

  requestHistoryJobsPage({ request, query }) {
    if (request) {
      return this.apiClient({
        method: request.method && request.method.toUpperCase() || 'POST',
        headers: request.headers,
        data: request.body || {},
        url: `${this.historyUri}${query || ''}`,
      });
    }
    return this.apiClient[request.method ? request.method.toLowerCase() : 'get'](`${this.historyUri}${query || ''}`);
  }

  getHistoryJobApps() {
    return this.requestDefaultHistoryJobs().then((res) => {
      const apps = {};
      res.data.forEach((job) => {
        if (job.properties.appName) {
          apps[job.properties.appName] = true;
        }
      });
      return Object.keys(apps);
    });
  }

  requestDefaultHistoryJobs() {
    return this.apiClient.get(`${this.historyUri}?length=200`);
  }

  //
  // Job metrics
  //
  subscribeJobMetrics(job, subscriber) {
    const { id } = job;
    let node = this.jobMetrics[id];
    if (!node) {
      node = {
        first: true,
        index: null,
        final: false,
        metrics: [],
        midx: job.modelInfo ? job.modelInfo.index : 0,
        subscribers: [subscriber],
        stop: () => {
          if (node.timeOutId) {
            clearTimeout(node.timeOutId);
            delete node.timeOutId;
          }
        },
        update: () => {
          const query = {};
          if (node.first) {
            query.b = true;
          } else {
            query.p = node.index;
          }
          query.m = node.midx;
          this.requestJobMetrics(job, query).then((res) => {
            try {
              const index = res.headers['x-gurobi-index'];
              node.index = typeof index === 'string' ? parseInt(index, 10) : index;
              node.final = node.index === -1;
              if (!node.startedAt) {
                const curJob = job;
                node.startedAt = curJob.modelInfo ? curJob.modelInfo.ts : curJob.startedAt;
              }
              let { data } = res;
              if (typeof data !== 'string') {
                // String data is automatically converted into a JSON by AXIOS when index === -1 in dev env
                console.log('\nData error!!!');
                console.log(data);
                data = JSON.stringify(data);
              }
              const lines = data ? data.split('\n') : [];
              const newRows = [];
              lines.forEach((line) => {
                if (line !== '') {
                  let v;
                  try {
                    v = JSON.parse(line);
                  } catch (e) {
                    console.error(e);
                    return e;
                  }
                  node.metrics.push(v);
                  newRows.push(v);
                  return v;
                }
                return null;
              });
              node.subscribers.forEach(s => s.onUpdate(newRows, node));
              if (node.first) {
                node.first = false;
                if (!node.final) {
                  node.interval = setInterval(() => node.update(), 2000);
                }
              } else if (node.final) { // end of update
                clearInterval(node.interval);
                node.interval = null;
              }
            } catch (e) {
              console.log(e);
            }
          }).catch((e) => {
            node.subscribers.forEach(s => s.onError(e));
          });
        },
      };
      this.jobMetrics[id] = node;
      if (job.mipInfo) {
        this.fetchJobParameters(job.id, job)
          .then((params) => {
            const mipGap = params.find(p => p.name === 'MIPGap');
            if (mipGap) {
              node.mipGap = Number(mipGap.value);
              node.subscribers.forEach((s) => {
                if (s.setMipGap) {
                  s.setMipGap(node.mipGap);
                }
              });
            }
            node.update();
          })
          .catch((e) => {
            node.subscribers.forEach(s => s.onError(e));
          });
      } else {
        node.update();
      }
    } else {
      node.subscribers.push(subscriber);
      subscriber.onUpdate(node.metrics, node);
      return node;
    }
    return node.status;
  }

  unsubscribeJobMetrics(job, subscriber) {
    const { id } = job;
    const node = this.jobMetrics[id];
    if (node) {
      const index = node.subscribers.indexOf(subscriber);
      if (index >= 0) {
        node.subscribers.splice(index, 1);
        if (node.subscribers.length === 0) {
          this.jobMetrics[id].stop();
          delete this.jobMetrics[id];
        }
      }
    }
  }

  requestJobMetrics() {
    throw new Error('Not implemented!');
  }

  // Errors
  setErrorHandler(onError) {
    this.onError = onError;
  }

  //
  // Job properties
  //
  getJobStatuses() {
    return JOB_STATUSES;
  }

  getJobStatus(job) { return job.status; }

  getJobStatusLabel(job) {
    const status = this.getJobStatus(job);
    return status ? this.statusLabels[status] : '';
  }

  getJobSolveStatusLabel(solveStatus, job) {
    switch (solveStatus) {
      case 1:
        return 'INIT';
      case 2:
        return 'OPTIMIZING';
      case 3:
        if (job && job.optimizationStatus) {
          switch (job.optimizationStatus.status) {
            case 1:
              return 'LOADED';
            case 2:
              return 'OPTIMAL';
            case 3:
              return 'INFEASIBLE';
            case 4:
              return 'INF_OR_UNBD';
            case 5:
              return 'UNBOUNDED';
            case 6:
              return 'CUTOFF';
            case 7:
              return 'ITERATION_LIMIT';
            case 8:
              return 'NODE_LIMIT';
            case 9:
              return 'TIME_LIMIT';
            case 10:
              return 'SOLUTION_LIMIT';
            case 11:
              return 'INTERRUPTED';
            case 12:
              return 'NUMERIC';
            case 13:
              return 'SUBOPTIMAL';
            case 14:
              return 'INPROGRESS';
            case 15:
              return 'USER_OBJ_LIMIT';
            default:
              return 'COMPLETED';
          }
        } else {
          return 'COMPLETED';
        }
      case 0:
        return 'UNKNOWN';
      default:
        return 'UNKNOWN';
    }
  }

  getJobStatusTime(job) {
    if (job.endedAt) {
      return job.endedAt;
    }
    if (job.startedAt) {
      return job.startedAt;
    }
    return job.queuedAt || 0;
  }

  getJobSolveStatuses() {
    return JOB_SOLVE_STATUSES;
  }

  getJobSolveStatus(job) {
    return job.solveStatus;
  }

  getJobName(job) { return job.id; }

  getJobParentId(job) { return job.properties ? job.properties.parentId : undefined; }

  getJobDistributedWorkerCount(job) { return job.nbDistributedWorkers || 0; }

  getJobUsername(job) { return job.properties.username; }

  getJobUserId(job) { return job.properties && job.properties.username; }

  useApplication() { return true; }

  getJobApplication(job) { return job.properties.appName; }

  getJobAddress(job) { return job.address; }

  getJobGroup(job) { return job.properties.group; }

  getJobStartedAt(job) { return job.startedAt; }

  getJobEndedAt(job) { return job.endedAt; }

  getJobQueuedAt(job) { return job.queuedAt; }

  getJobAlgorithm(job) {
    if (job.simplexInfo) {
      return 'SIMPLEX';
    }
    if (job.mipInfo) {
      return 'MIP';
    }
    if (job.barrierInfo) {
      return 'BARRIER';
    }
    if (job.tuningInfo) {
      return 'TUNING';
    }
    return '';
  }

  getJobRuntime(job) {
    return job.properties && job.properties.runtime;
  }

  getErrorMessage(job) {
    return job.errorMsg;
  }

  getErrorCode(job) {
    return job.errorCode;
  }

  getError(job) {
    // Expect to return a readonly object with errorMsg and errorCode props
    // which are conveniently job's own properties for errors
    return job;
  }

  // Batch method
  useBatch() { return this.batch; }

  getJobBatchId(job) { return job.properties.batchId; }
}

export {
  JobService as default,
  JOB_STATUS_QUEUED_VALUE,
  JOB_STATUS_UNKOWN,
  JOB_STATUS_QUEUED,
  JOB_STATUS_RUNNING,
  JOB_STATUS_ABORTED,
  JOB_STATUS_FAILED,
  JOB_STATUS_COMPLETED,
  JOB_STATUS_REJECTED,
  JOB_STATUS_DISCONNECTED,
  JOB_STATUS_IDLETIMEOUT,
  MAIN_JOBS,
  FILTERED_MAIN_JOBS,
  QUEUED_JOBS,
  HISTORY_JOBS,
  JOB_TYPES,
};
